/**
 * Generieke edit-modal voor pijler / rock / propositie / resource-request.
 * Krijgt een `schema` (lijst van velden), het entity-object (of null voor nieuw),
 * en `onSave(values)` / `onDelete()`.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, IconButton, Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { T } from './strategyTokens';
import { bevestig } from '../../utils/bevestig';

export default function StrategyEditModal({
  open, onClose, title, schema, entity, onSave, onDelete, saving = false,
}) {
  const [values, setValues] = useState({});
  // Schema wordt bij elke render van de ouder opnieuw opgebouwd (bv. als de personenlijst binnenkomt);
  // alleen bij openen of een andere entiteit opnieuw vullen, anders gaat lopende invoer verloren.
  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  useEffect(() => {
    if (!open) return;
    const initial = {};
    for (const field of schemaRef.current) {
      const raw = entity?.[field.name];
      initial[field.name] = raw == null ? (field.defaultValue ?? '') : raw;
    }
    setValues(initial);
  }, [open, entity]);

  const setField = (name) => (e) => {
    const v = e?.target ? e.target.value : e;
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    // Strip lege strings naar null voor optionele velden
    const cleaned = {};
    for (const field of schema) {
      let v = values[field.name];
      if (v === '' && !field.required) v = null;
      if (field.type === 'number' && v != null && v !== '') v = Number(v);
      if (field.type === 'boolean') v = !!v;
      cleaned[field.name] = v;
    }
    onSave(cleaned);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
      sx: { borderRadius: '0.75rem', bgcolor: T.surface },
    }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: T.font, fontWeight: 800, color: T.text, fontSize: '1.1rem',
        py: 2, borderBottom: `1px solid ${T.surfaceHigh}`,
      }}>
        {title}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {schema.map((field) => (
            <FieldInput
              key={field.name}
              field={field}
              value={values[field.name]}
              onChange={setField(field.name)}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{
          px: 3, py: 2, borderTop: `1px solid ${T.surfaceHigh}`,
          display: 'flex', justifyContent: 'space-between',
        }}>
          <Box>
            {entity?.id && onDelete && (
              <Button
                onClick={async () => {
                  if (await bevestig({ titel: 'Item verwijderen?', tekst: 'Dit verwijdert het item permanent.', knop: 'Verwijderen', gevaarlijk: true })) {
                    onDelete();
                  }
                }}
                startIcon={<DeleteOutlineIcon />}
                sx={{
                  color: T.red, textTransform: 'none',
                  fontFamily: T.fontBody, fontWeight: 600,
                  '&:hover': { bgcolor: T.redBg },
                }}
                disabled={saving}
              >
                Verwijder
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} sx={{
              textTransform: 'none', color: T.secondary,
              fontFamily: T.fontBody, fontWeight: 600,
            }}>
              Annuleer
            </Button>
            <Button
              type="submit" variant="contained" disableElevation disabled={saving}
              sx={{
                textTransform: 'none', bgcolor: T.tertiary, fontFamily: T.fontBody,
                fontWeight: 700, px: 2.5, borderRadius: '0.5rem',
                '&:hover': { bgcolor: T.primaryDim },
              }}
            >
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type === 'select') {
    return (
      <TextField
        select label={field.label} value={value ?? ''} onChange={onChange}
        size="small" fullWidth required={field.required}
        InputLabelProps={{ sx: { fontFamily: T.fontBody } }}
        sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.9rem' } }}
      >
        {!field.required && <MenuItem value=""><em>—</em></MenuItem>}
        {field.options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>
    );
  }
  if (field.type === 'boolean') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 18, height: 18, accentColor: T.tertiary, cursor: 'pointer' }}
        />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.9rem', color: T.text }}>
          {field.label}
        </Typography>
      </Box>
    );
  }
  if (field.type === 'progress') {
    return (
      <Box>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mb: 0.5 }}>
          {field.label}: {value ?? 0}%
        </Typography>
        <Slider
          value={Number(value) || 0}
          onChange={(_, v) => onChange(v)}
          min={0} max={100} step={5}
          sx={{ color: T.tertiary }}
        />
      </Box>
    );
  }
  if (field.type === 'textarea') {
    return (
      <TextField
        label={field.label} value={value ?? ''} onChange={onChange}
        size="small" fullWidth multiline minRows={field.rows || 3}
        required={field.required}
        InputLabelProps={{ sx: { fontFamily: T.fontBody } }}
        sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.88rem' } }}
      />
    );
  }
  if (field.type === 'date') {
    return (
      <TextField
        type="date" label={field.label} value={value ?? ''} onChange={onChange}
        size="small" fullWidth InputLabelProps={{ shrink: true, sx: { fontFamily: T.fontBody } }}
        sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.9rem' } }}
      />
    );
  }
  return (
    <TextField
      label={field.label} value={value ?? ''} onChange={onChange}
      size="small" fullWidth required={field.required}
      InputLabelProps={{ sx: { fontFamily: T.fontBody } }}
      sx={{ '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.9rem' } }}
    />
  );
}
